import { Button, Divider, Form, Input } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { FORM } from "@/constants/DatasetTypes";
import { INPUT_MAX_LENGTH } from "@/constants/Fields";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import { useSidePanel } from "@/modules/side-panel";
import { createRandomId } from "@/utilities/id";

import { TreeModifier } from "./TreeModifier";

const { useForm } = Form;

const AddDatasetTemplateButton = ({ parentTemplate, type, layoutIndex }) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const { closeSidePanel } = useSidePanel();
  const { onError } = useApiErrorHandler();

  const updateMutation = useUpdate("interventionTemplates", parentTemplate?.id);

  function onAdd() {
    if (!hasValidSection()) {
      return;
    }

    const templateData = {
      name: form.getFieldValue("templateName"),
      description: form.getFieldValue("description"),
      type: type,
      layout_index: createRandomId(),
    };

    if (type === FORM) {
      templateData.form = null;
      templateData.model = null;
    }

    const updatedTree = TreeModifier.findAndAppendChild(
      parentTemplate,
      "templates",
      (template) =>
        (!layoutIndex && !template.layout_index) ||
        template.layout_index === layoutIndex,
      templateData
    );

    updateMutation.mutate(
      { templates: updatedTree.templates },
      {
        onSuccess: function () {
          onClose();
        },
        onError,
      }
    );
  }

  function onClose() {
    form.resetFields();
    closeSidePanel();
  }

  function hasValidSection() {
    form.validateFields();

    // cast to boolean value so undefined becomes false
    return !!form.getFieldValue("templateName");
  }

  const ContentFragment = () => (
    <Form form={form} layout="vertical">
      <Form.Item
        name="templateName"
        label={t("interventionTemplate:name.label")}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input
          placeholder={t("interventionTemplate:name.label")}
          maxLength={INPUT_MAX_LENGTH}
          showCount
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("interventionTemplate:description:label")}
      >
        <Input
          data-testid="description"
          placeholder={t("interventionTemplate:description:label")}
        />
      </Form.Item>
    </Form>
  );

  const ExtraFragment = () => (
    <>
      <Button onClick={onClose}>{t("global:cancel")}</Button>
      <Button onClick={onAdd} type="primary" loading={updateMutation.isPending}>
        {t("global:add")}
      </Button>
    </>
  );

  return (
    <>
      <ContentFragment />
      <Divider />
      <ExtraFragment />
    </>
  );
};

AddDatasetTemplateButton.propTypes = {
  parentTemplate: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  layoutIndex: PropTypes.number.isRequired,
};

export default AddDatasetTemplateButton;
